import { Box, Divider, List, ListSubheader, useTheme } from '@mui/material';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import T4loLogoIcon from '../../_UnderConstruction/T4loLogoIcon';
import ProfileAvatar from '../../General/ProfileAvatar/ProfileAvatar';
import { useNavigation } from './NavigationContext';
import MobileSidebar from './mobile-sidebar';
import { NavigationItem } from './NavigationItem';

type NavigationMode = 'hidden' | 'collapsed' | 'expanded';

interface NavigationProps {
    loggedIn: boolean;
    mode: NavigationMode;
    isMobile: boolean;
    onToggle: () => void;
    setMode: (mode: NavigationMode) => void;
    mobileSidebarOpen: boolean;
    setMobileSidebarOpen: (open: boolean) => void;
}

function Navigation(props: NavigationProps) {
    const theme = useTheme();

    const navClass = {
        hidden: 'navIsClosed',
        collapsed: 'navCollapsed',
        expanded: 'navIsOpen',
    }[props.mode];

    const isOpen = props.mode !== 'hidden';
    const isCollapsed = props.mode === 'collapsed';

    if (!props.loggedIn) return <></>;

    return (
        <>
            <MobileSidebar
                open={props.mobileSidebarOpen}
                onClose={() => props.setMobileSidebarOpen(false)}
                loggedIn={props.loggedIn}
            />

            {!props.isMobile && (
                <div className={`navigation ${navClass}`}>
                    <Box
                        sx={{
                            width: isCollapsed ? 72 : 270,
                            minWidth: isCollapsed ? 72 : 270,
                            maxWidth: isCollapsed ? 72 : 270,
                            height: '100%',
                            display: isOpen ? 'flex' : 'none',
                            flexDirection: 'column',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            borderRight: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                            transition: theme.transitions.create(['width', 'min-width', 'max-width'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            boxSizing: 'border-box',
                            position: 'relative',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <DesktopNavItems
                                isMobile={props.isMobile}
                                mode={props.mode}
                                onToggle={props.onToggle}
                                setMode={props.setMode}
                            />
                        </Box>
                    </Box>
                </div>
            )}
        </>
    );
}

export default Navigation;

interface DesktopNavItemsProps {
    isMobile: boolean;
    mode: NavigationMode;
    onToggle: () => void;
    setMode: (mode: NavigationMode) => void;
}

function DesktopNavItems(props: DesktopNavItemsProps) {
    const theme = useTheme();
    const { navItems } = useNavigation();
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    const isCollapsed = props.mode === 'collapsed';

    const onClick = () => {
        if (props.isMobile) {
            props.setMode('hidden');
        }
    };

    const mainItems = navItems.filter((item) => item.group === 'main');
    const salesItems = navItems.filter((item) => item.group === 'sales');

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: props.mode === 'collapsed' ? 'center' : 'flex-start',
                    alignItems: 'center',
                    px: 2,
                    minHeight: { xs: 64, sm: 72 },
                    maxHeight: { xs: 64, sm: 72 },
                    width: '100%',
                    position: 'relative',
                }}
            >
                <Link to={`/${ELinks.DASHBOARD}`} style={{ textDecoration: 'none' }}>
                    <T4loLogoIcon
                        width={48}
                        height={48}
                        sx={{
                            cursor: 'pointer',
                            p: 1,
                        }}
                    />
                </Link>
                {!isCollapsed && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Beta
                    </Box>
                )}
            </Box>

            <Divider />
            <ProfileAvatar isCollapsed={isCollapsed} />

            <List
                sx={{
                    p: isCollapsed ? 0 : 1,
                    flexGrow: 1,
                    '& .MuiListItemButton-root': {
                        borderRadius: 1,
                        transition: theme.transitions.create(['background-color', 'padding'], {
                            duration: theme.transitions.duration.standard,
                        }),
                    },
                }}
            >
                {mainItems.map((item) => (
                    <NavigationItem
                        key={item.id}
                        onClick={onClick}
                        path={item.path}
                        title={item.title}
                        icon={item.icon}
                        forceActive={item.forceActive}
                        activeColor={item.activeColor}
                    />
                ))}

                {salesItems.length > 0 && featureFlags.opportunities && (
                    <>
                        <Divider sx={{ my: 1, mx: 2 }} />

                        {!isCollapsed && (
                            <ListSubheader
                                sx={{
                                    backgroundColor: 'transparent',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    px: 2,
                                    py: 1,
                                    lineHeight: 1.5,
                                }}
                            >
                                Vertrieb
                            </ListSubheader>
                        )}

                        {salesItems.map((item) => (
                            <NavigationItem
                                key={item.id}
                                onClick={onClick}
                                path={item.path}
                                title={item.title}
                                icon={item.icon}
                            />
                        ))}
                    </>
                )}
            </List>
        </>
    );
}
